// `common/services/internal-analytics.ng1.ts` — careero's own action tracking, the two calls
// every shortlist analytics event ends in. Served by `ActionTrackingAPI.java`
// (`@RequestMapping("track")`), which is NOT under `/chrome-extension-api`: the proxy forwards
// `/track` for exactly this (`apps/spa/src/proxy-paths.ts`).
//
// ⚠️ `url` IS AN ANALYTICS CONTRACT, NOT A REQUEST URL. Upstream fills it with
// `$analytics.settings.pageTracking.basePath + $location.url()` (`internal-analytics.ng1.ts:60`,
// `:65`), and careero's `WooActionTrackingDataImporter` / `ActionTrackingHelperService`
// prefix-match it (CLAUDE.md constraint 1, `packages/protocol/url-contracts.json`). This file
// sends what it is given; `trackingPageUrl()` in `@woo-ext/ui` is the one place that builds it.
import { request } from './request.ts'

/**
 * `ITrackEventWithUrlParams` / `ITrackEventWithUrlBulkParamsDTO` (`internal-analytics.ng1.ts:13`,
 * `:23`). Every field is a STRING on the client — `value` included, although the server binds it
 * as `Integer`, so a non-numeric `value` is a 400 there.
 */
export interface TrackEventWithUrl {
  url: string
  category: string
  action: string
  label?: string
  /**
   * `null` and absent are different on the bulk wire (`"value": null` against no key) and the
   * same on the single one (`$httpParamSerializer` drops both). The add-to-job and message
   * services send `null`; everything else sends nothing.
   */
  value?: string | null
  extraInfo?: string
}

/** `ITrackEventWithUrlBulkParamsRequestDTO` (`internal-analytics.ng1.ts:32`). */
export interface TrackEventBulkRequest {
  actions: TrackEventWithUrl[]
  overrideUUID?: string
}

/**
 * `trackEventWithUrl` (`internal-analytics.ng1.ts:71`) — `GET /track/l`, every field in the
 * QUERY. `$httpParamSerializer` sorts the keys and drops `null`/`undefined`; this does the same,
 * so the request line is the one upstream sends.
 *
 * Errors are not shown: upstream's interceptor exempts `/track/l` only for status 0
 * (`http.error.interceptor.ng1.ts:38`), but a tracking call that pops the failure dialog over the
 * action it was recording is worse than one that fails silently — and the caller never awaits it.
 */
export async function trackEventWithUrl(params: TrackEventWithUrl & { overrideUUID?: string }): Promise<void> {
  const query = new URLSearchParams()
  for (const key of Object.keys(params).sort()) {
    const value = params[key as keyof typeof params]
    if (value !== undefined && value !== null) query.append(key, value)
  }
  await request<null>('GET', `/track/l?${query.toString()}`, undefined, {}, { suppressErrorDialog: true })
}

/**
 * `trackEventWithUrlBulk` (`internal-analytics.ng1.ts:78`) — `POST /track/logBulk`, the request
 * as the JSON body, an empty 200 back. The server returns early on an empty `actions`, so an
 * empty list is sent rather than skipped, as upstream does.
 */
export async function trackEventWithUrlBulk(body: TrackEventBulkRequest): Promise<void> {
  await request<null>('POST', '/track/logBulk', body, {}, { suppressErrorDialog: true })
}
